import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { tiktokAPI, getAssetUrl } from "../lib/api";
import { formatNumber } from "../lib/utils";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Switch } from "../components/ui/switch";
import {
  Loader2,
  Link as LinkIcon,
  Unlink,
  Users,
  Heart,
  Eye,
  MessageCircle,
  Share2,
  TrendingUp,
  BarChart3,
  Film,
  Clock,
  Download,
  ExternalLink,
  CheckCircle,
  AlertCircle,
  Plus,
  Star,
  MoreVertical,
  Briefcase,
  User,
  Sparkles,
} from "lucide-react";

// TikTok Logo Component
const TikTokLogo = ({ className = "w-6 h-6" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

// Account type icons
const accountTypeIcons = {
  creator: <Sparkles className="w-3 h-3" />,
  business: <Briefcase className="w-3 h-3" />,
  personal: <User className="w-3 h-3" />,
};

const accountTypeLabels = {
  creator: "Creator",
  business: "Business",
  personal: "Personal",
};

export default function TikTokConnectionPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [oauthAccounts, setOauthAccounts] = useState([]); // OAuth connected accounts
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [videos, setVideos] = useState([]);
  const [connectDialogOpen, setConnectDialogOpen] = useState(false);
  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);
  const [accountToDisconnect, setAccountToDisconnect] = useState(null);
  const [username, setUsername] = useState("");
  const [accountType, setAccountType] = useState("creator");
  const [setAsDefault, setSetAsDefault] = useState(false);
  const [sortBy, setSortBy] = useState("views");
  const [importingVideo, setImportingVideo] = useState(null);
  const [settingDefault, setSettingDefault] = useState(null);
  const [oauthConnecting, setOauthConnecting] = useState(false);

  // Listen for OAuth callback messages
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data.type === "tiktok-oauth-success") {
        toast.success(`Connected to TikTok as ${event.data.display_name}!`);
        setOauthConnecting(false);
        // Refresh OAuth accounts list
        tiktokAPI.getOAuthAccounts().then(response => {
          setOauthAccounts(response.data || []);
        }).catch(console.error);
      } else if (event.data.type === "tiktok-oauth-error") {
        toast.error(`TikTok connection failed: ${event.data.message}`);
        setOauthConnecting(false);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  const fetchOAuthAccounts = useCallback(async () => {
    try {
      const response = await tiktokAPI.getOAuthAccounts();
      setOauthAccounts(response.data || []);
      return response.data || [];
    } catch (error) {
      console.error("Failed to fetch OAuth accounts:", error);
      return [];
    }
  }, []);

  const fetchAccounts = useCallback(async () => {
    try {
      const response = await tiktokAPI.getAccounts();
      const accountsList = response.data || [];
      setAccounts(accountsList);
      
      // Auto-select the default account or the first one
      if (accountsList.length > 0) {
        const defaultAcc = accountsList.find(a => a.is_default) || accountsList[0];
        setSelectedAccount(defaultAcc);
      } else {
        setSelectedAccount(null);
      }
      return accountsList;
    } catch (error) {
      console.error("Failed to fetch accounts:", error);
      return [];
    }
  }, []);

  const fetchAnalyticsAndVideos = useCallback(async (accountId) => {
    if (!accountId) return;
    try {
      const [analyticsRes, videosRes] = await Promise.all([
        tiktokAPI.getAnalytics(accountId),
        tiktokAPI.getVideos(20, sortBy, accountId),
      ]);
      setAnalytics(analyticsRes.data);
      setVideos(videosRes.data);
    } catch (error) {
      console.error("Failed to fetch analytics/videos:", error);
    }
  }, [sortBy]);

  useEffect(() => {
    const loadData = async () => {
      const [accountsList] = await Promise.all([
        fetchAccounts(),
        fetchOAuthAccounts()
      ]);
      if (accountsList.length > 0) {
        const defaultAcc = accountsList.find(a => a.is_default) || accountsList[0];
        await fetchAnalyticsAndVideos(defaultAcc.id);
      }
      setLoading(false);
    };
    loadData();
  }, [fetchAccounts, fetchAnalyticsAndVideos, fetchOAuthAccounts]);

  useEffect(() => {
    if (selectedAccount) {
      fetchAnalyticsAndVideos(selectedAccount.id);
    }
  }, [selectedAccount, sortBy, fetchAnalyticsAndVideos]);

  // OAuth Connect handler - opens TikTok OAuth popup
  const handleOAuthConnect = async () => {
    setOauthConnecting(true);
    try {
      const response = await tiktokAPI.getOAuthLoginUrl();
      const authUrl = response.data.auth_url;
      
      // Open TikTok OAuth in popup
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      
      window.open(
        authUrl,
        "TikTok Login",
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`
      );
    } catch (error) {
      console.error("Failed to get TikTok auth URL:", error);
      toast.error("Failed to connect to TikTok. Please try again.");
      setOauthConnecting(false);
    }
  };

  // Disconnect OAuth account
  const handleDisconnectOAuth = async (accountId) => {
    try {
      await tiktokAPI.disconnectOAuth(accountId);
      toast.success("TikTok account disconnected");
      fetchOAuthAccounts();
    } catch (error) {
      toast.error("Failed to disconnect TikTok account");
    }
  };

  const handleConnect = async () => {
    if (!username) {
      toast.error("Please enter your TikTok username");
      return;
    }

    setConnecting(true);
    try {
      const response = await tiktokAPI.connect(username, accountType, setAsDefault);
      setConnectDialogOpen(false);
      setUsername("");
      setAccountType("creator");
      setSetAsDefault(false);
      toast.success(`TikTok account @${response.data.tiktok_username} connected!`);
      
      // Refresh accounts list
      const accountsList = await fetchAccounts();
      // Select the newly connected account
      const newAccount = accountsList.find(a => a.tiktok_username === response.data.tiktok_username);
      if (newAccount) {
        setSelectedAccount(newAccount);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to connect TikTok");
    } finally {
      setConnecting(false);
    }
  };

  const handleSetDefault = async (accountId) => {
    setSettingDefault(accountId);
    try {
      await tiktokAPI.setDefaultAccount(accountId);
      toast.success("Default account updated!");
      await fetchAccounts();
    } catch (error) {
      toast.error("Failed to update default account");
    } finally {
      setSettingDefault(null);
    }
  };

  const handleDisconnect = async () => {
    if (!accountToDisconnect) return;
    
    try {
      await tiktokAPI.disconnectAccount(accountToDisconnect.id);
      toast.success(`TikTok account @${accountToDisconnect.tiktok_username} disconnected`);
      setDisconnectDialogOpen(false);
      setAccountToDisconnect(null);
      
      // Refresh accounts
      const accountsList = await fetchAccounts();
      if (accountsList.length === 0) {
        setAnalytics(null);
        setVideos([]);
      }
    } catch (error) {
      toast.error("Failed to disconnect");
    }
  };

  const handleImportVideo = async (videoId) => {
    setImportingVideo(videoId);
    try {
      await tiktokAPI.importToLibrary(videoId);
      toast.success("Video imported to your library!");
    } catch (error) {
      toast.error("Failed to import video");
    } finally {
      setImportingVideo(null);
    }
  };

  const openDisconnectDialog = (account) => {
    setAccountToDisconnect(account);
    setDisconnectDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#00F0FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // No accounts connected state
  if (accounts.length === 0 && oauthAccounts.length === 0) {
    return (
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="max-w-2xl mx-auto space-y-8"
        data-testid="tiktok-connection-page"
      >
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight flex items-center gap-3">
            <TikTokLogo className="w-8 h-8" />
            Link TikTok Accounts
          </h1>
          <p className="text-muted-foreground mt-2">
            Connect your TikTok accounts to import videos and view analytics.
          </p>
        </div>

        <motion.div variants={itemVariants}>
          <Card className="bg-card border-border/50">
            <CardContent className="py-16 text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-[#FF0050] to-[#00F2EA] flex items-center justify-center">
                <TikTokLogo className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Connect Your TikTok</h2>
              <p className="text-muted-foreground max-w-md mx-auto mb-8">
                Link your TikTok accounts to post videos directly, import your existing content, 
                and manage everything from one place.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-lg mx-auto mb-8 text-left">
                <div className="flex items-start gap-3 p-3 bg-secondary/30 rounded-lg">
                  <Film className="w-5 h-5 text-[#00F0FF] mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-white">Post Videos</p>
                    <p className="text-xs text-muted-foreground">Share directly to TikTok</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-secondary/30 rounded-lg">
                  <BarChart3 className="w-5 h-5 text-[#FF0055] mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-white">Analytics</p>
                    <p className="text-xs text-muted-foreground">Track performance</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-secondary/30 rounded-lg">
                  <Users className="w-5 h-5 text-[#00FF94] mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-white">Multi-Account</p>
                    <p className="text-xs text-muted-foreground">Manage all profiles</p>
                  </div>
                </div>
              </div>

              {/* OAuth Connect Button (Primary) */}
              <Button
                onClick={handleOAuthConnect}
                disabled={oauthConnecting}
                className="bg-gradient-to-r from-[#FF0050] to-[#00F2EA] hover:opacity-90 text-white font-bold rounded-xl gap-2 px-8 mb-4"
                data-testid="connect-tiktok-oauth-btn"
              >
                {oauthConnecting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Connecting...
                  </>
                ) : (
                  <>
                    <TikTokLogo className="w-5 h-5" />
                    Connect with TikTok
                  </>
                )}
              </Button>

              <p className="text-xs text-muted-foreground mt-4">
                Secure connection using TikTok's official login
              </p>
            </CardContent>
          </Card>
        </motion.div>

        {/* Connect Dialog (Legacy - keeping for backwards compatibility) */}
        <ConnectDialog
          open={connectDialogOpen}
          onOpenChange={setConnectDialogOpen}
          username={username}
          setUsername={setUsername}
          accountType={accountType}
          setAccountType={setAccountType}
          setAsDefault={setAsDefault}
          setSetAsDefault={setSetAsDefault}
          connecting={connecting}
          onConnect={handleConnect}
          isFirstAccount={true}
        />
      </motion.div>
    );
  }

  // Connected state with accounts
  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-8"
      data-testid="tiktok-connected-page"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight flex items-center gap-3">
            <TikTokLogo className="w-8 h-8" />
            TikTok Accounts
          </h1>
          <p className="text-muted-foreground mt-2">
            Manage your connected TikTok accounts and view analytics.
          </p>
        </div>
        <Button
          onClick={handleOAuthConnect}
          disabled={oauthConnecting}
          className="bg-gradient-to-r from-[#FF0050] to-[#00F2EA] hover:opacity-90 text-white font-medium rounded-xl gap-2"
          data-testid="add-account-btn"
        >
          {oauthConnecting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Connecting...
            </>
          ) : (
            <>
              <Plus className="w-4 h-4" />
              Add Account
            </>
          )}
        </Button>
      </div>

      {/* Accounts List */}
      <motion.div variants={itemVariants}>
        <Card className="bg-card border-border/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-[#00F0FF]" />
              Connected Accounts ({accounts.length})
            </CardTitle>
            <CardDescription>
              Select an account to view its analytics and videos.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {accounts.map((account) => (
                <div
                  key={account.id}
                  className={`relative p-4 rounded-xl border-2 transition-all cursor-pointer ${
                    selectedAccount?.id === account.id
                      ? "border-[#00F0FF] bg-[#00F0FF]/10"
                      : "border-border/50 bg-secondary/20 hover:border-border"
                  }`}
                  onClick={() => setSelectedAccount(account)}
                  data-testid={`account-card-${account.tiktok_username}`}
                >
                  {/* Default Badge */}
                  {account.is_default && (
                    <Badge className="absolute -top-2 -right-2 bg-[#00F0FF] text-[#0A0A0B] gap-1">
                      <Star className="w-3 h-3" />
                      Default
                    </Badge>
                  )}
                  
                  {/* Account Menu */}
                  <div className="absolute top-2 right-2">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {!account.is_default && (
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSetDefault(account.id);
                            }}
                            disabled={settingDefault === account.id}
                          >
                            {settingDefault === account.id ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <Star className="w-4 h-4 mr-2" />
                            )}
                            Set as Default
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            openDisconnectDialog(account);
                          }}
                          className="text-destructive focus:text-destructive"
                        >
                          <Unlink className="w-4 h-4 mr-2" />
                          Disconnect
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  <div className="flex items-center gap-3 mb-3">
                    <img
                      src={account.tiktok_avatar}
                      alt={account.tiktok_display_name}
                      className="w-12 h-12 rounded-full border-2 border-white/20"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-white truncate">{account.tiktok_display_name}</p>
                      </div>
                      <p className="text-sm text-muted-foreground">@{account.tiktok_username}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 mb-3">
                    <Badge variant="outline" className="text-xs gap-1">
                      {accountTypeIcons[account.account_type]}
                      {accountTypeLabels[account.account_type]}
                    </Badge>
                    <Badge className="badge-success gap-1 text-xs">
                      <CheckCircle className="w-3 h-3" />
                      Connected
                    </Badge>
                  </div>

                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <p className="text-lg font-bold text-white">{formatNumber(account.follower_count)}</p>
                      <p className="text-xs text-muted-foreground">Followers</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-white">{formatNumber(account.likes_count)}</p>
                      <p className="text-xs text-muted-foreground">Likes</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-white">{account.video_count}</p>
                      <p className="text-xs text-muted-foreground">Videos</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* OAuth Connected Accounts - Now integrated as primary accounts */}
      {oauthAccounts.length > 0 && (
        <motion.div variants={itemVariants}>
          <Card className="bg-card border-border/50 border-green-500/30">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                TikTok Connected (OAuth) ({oauthAccounts.length})
              </CardTitle>
              <CardDescription>
                Connected via official TikTok login - ready to post videos directly to TikTok.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {oauthAccounts.map((account) => (
                  <div
                    key={account.id}
                    className="relative p-4 rounded-xl border-2 border-green-500/50 bg-green-500/5 hover:bg-green-500/10 transition-all"
                  >
                    <Badge className="absolute -top-2 -right-2 bg-green-500 text-white gap-1">
                      <CheckCircle className="w-3 h-3" />
                      OAuth
                    </Badge>
                    
                    <div className="flex items-center gap-3 mb-3">
                      {account.tiktok_avatar ? (
                        <img
                          src={account.tiktok_avatar}
                          alt={account.tiktok_display_name}
                          className="w-12 h-12 rounded-full border-2 border-white/20"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#FF0050] to-[#00F2EA] flex items-center justify-center">
                          <TikTokLogo className="w-6 h-6 text-white" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-white truncate">{account.tiktok_display_name}</p>
                        <p className="text-sm text-muted-foreground">@{account.tiktok_username}</p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between mb-3">
                      <Badge className="badge-success gap-1 text-xs">
                        <CheckCircle className="w-3 h-3" />
                        Ready to Post
                      </Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDisconnectOAuth(account.id)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      >
                        <Unlink className="w-4 h-4" />
                      </Button>
                    </div>

                    {/* Post to TikTok button */}
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full border-green-500/50 text-green-400 hover:bg-green-500/10"
                      onClick={() => navigate('/videos')}
                    >
                      <Film className="w-4 h-4 mr-2" />
                      Go to Videos to Post
                    </Button>
                  </div>
                ))}
              </div>
              
              {/* Info about OAuth accounts */}
              <div className="mt-4 p-3 bg-green-500/10 rounded-lg border border-green-500/20">
                <p className="text-sm text-green-400">
                  <CheckCircle className="w-4 h-4 inline mr-2" />
                  OAuth accounts can post videos directly to TikTok. Go to your Video Library and click "Share to TikTok" on any video.
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Selected Account Analytics */}
      {selectedAccount && (
        <Tabs defaultValue="overview" className="space-y-6">
          <div className="flex items-center justify-between">
            <TabsList className="bg-secondary/50">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="videos">Videos</TabsTrigger>
              <TabsTrigger value="audience">Audience</TabsTrigger>
            </TabsList>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>Viewing:</span>
              <Badge variant="outline" className="font-medium">
                @{selectedAccount.tiktok_username}
              </Badge>
            </div>
          </div>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: "Total Views", value: analytics?.total_views || 0, icon: Eye, color: "#00F0FF" },
                { label: "Total Likes", value: analytics?.total_likes || 0, icon: Heart, color: "#FF0055" },
                { label: "Comments", value: analytics?.total_comments || 0, icon: MessageCircle, color: "#00FF94" },
                { label: "Shares", value: analytics?.total_shares || 0, icon: Share2, color: "#A855F7" },
              ].map((stat) => (
                <motion.div key={stat.label} variants={itemVariants}>
                  <Card className="bg-card border-border/50">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <stat.icon className="w-5 h-5" style={{ color: stat.color }} />
                        <TrendingUp className="w-4 h-4 text-[#00FF94]" />
                      </div>
                      <p className="text-2xl font-bold text-white">{formatNumber(stat.value)}</p>
                      <p className="text-xs text-muted-foreground">{stat.label}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>

            {/* Top Video */}
            {analytics?.top_performing_video && (
              <motion.div variants={itemVariants}>
                <Card className="bg-card border-border/50">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-[#FF0055]" />
                      Top Performing Video
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex gap-4">
                      <img
                        src={getAssetUrl(analytics.top_performing_video.thumbnail_url)}
                        alt={analytics.top_performing_video.title}
                        className="w-32 h-48 object-cover rounded-lg"
                      />
                      <div className="flex-1">
                        <h3 className="font-semibold text-white mb-2">{analytics.top_performing_video.title}</h3>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Eye className="w-4 h-4" />
                            {formatNumber(analytics.top_performing_video.views)} views
                          </div>
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Heart className="w-4 h-4" />
                            {formatNumber(analytics.top_performing_video.likes)} likes
                          </div>
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <MessageCircle className="w-4 h-4" />
                            {formatNumber(analytics.top_performing_video.comments)} comments
                          </div>
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Clock className="w-4 h-4" />
                            {analytics.top_performing_video.duration}s
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Avg Watch Time */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <motion.div variants={itemVariants}>
                <Card className="bg-card border-border/50">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-[#00F0FF]/10 rounded-xl flex items-center justify-center">
                        <Clock className="w-6 h-6 text-[#00F0FF]" />
                      </div>
                      <div>
                        <p className="text-3xl font-bold text-white">{analytics?.avg_watch_time || 0}s</p>
                        <p className="text-muted-foreground">Avg Watch Time</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
              <motion.div variants={itemVariants}>
                <Card className="bg-card border-border/50">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-[#00FF94]/10 rounded-xl flex items-center justify-center">
                        <Users className="w-6 h-6 text-[#00FF94]" />
                      </div>
                      <div>
                        <p className="text-3xl font-bold text-white">+{analytics?.follower_growth || 0}</p>
                        <p className="text-muted-foreground">Follower Growth (30d)</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </div>
          </TabsContent>

          {/* Videos Tab */}
          <TabsContent value="videos" className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">Imported Videos ({videos.length})</h3>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-40 bg-secondary/50 border-border/50">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="views">Most Views</SelectItem>
                  <SelectItem value="likes">Most Likes</SelectItem>
                  <SelectItem value="comments">Most Comments</SelectItem>
                  <SelectItem value="created_at">Newest</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {videos.length === 0 ? (
              <div className="text-center py-12">
                <Film className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No videos found for this account</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {videos.map((video) => (
                  <div key={video.id}>
                    <Card className="bg-card border-border/50 card-hover overflow-hidden">
                      <div className="relative">
                        <img
                          src={getAssetUrl(video.thumbnail_url)}
                          alt={video.title}
                          className="w-full aspect-[9/16] object-cover"
                        />
                        <div className="absolute bottom-2 right-2 bg-black/70 px-2 py-1 rounded text-xs text-white">
                          {video.duration}s
                        </div>
                      </div>
                      <CardContent className="p-4">
                        <h4 className="font-medium text-white text-sm line-clamp-2 mb-2">{video.title}</h4>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground mb-3">
                          <span className="flex items-center gap-1">
                            <Eye className="w-3 h-3" />
                            {formatNumber(video.views)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Heart className="w-3 h-3" />
                            {formatNumber(video.likes)}
                          </span>
                          <span className="flex items-center gap-1">
                            <MessageCircle className="w-3 h-3" />
                            {formatNumber(video.comments)}
                          </span>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1 text-xs border-border/50"
                            onClick={() => window.open(video.video_url, "_blank")}
                          >
                            <ExternalLink className="w-3 h-3 mr-1" />
                            View
                          </Button>
                          <Button
                            size="sm"
                            className="flex-1 text-xs bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-[#0A0A0B]"
                            onClick={() => handleImportVideo(video.id)}
                            disabled={importingVideo === video.id}
                          >
                            {importingVideo === video.id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <>
                                <Download className="w-3 h-3 mr-1" />
                                Import
                              </>
                            )}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Audience Tab */}
          <TabsContent value="audience" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Age Distribution */}
              <motion.div variants={itemVariants}>
                <Card className="bg-card border-border/50">
                  <CardHeader>
                    <CardTitle className="text-lg font-semibold text-white">Age Distribution</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {Object.entries(analytics?.audience_demographics?.age_groups || {}).map(([age, percentage]) => (
                      <div key={age} className="space-y-1">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">{age}</span>
                          <span className="text-white font-medium">{percentage}%</span>
                        </div>
                        <Progress value={percentage} className="h-2" />
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </motion.div>

              {/* Top Countries */}
              <motion.div variants={itemVariants}>
                <Card className="bg-card border-border/50">
                  <CardHeader>
                    <CardTitle className="text-lg font-semibold text-white">Top Countries</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {(analytics?.audience_demographics?.top_countries || []).map((country, index) => (
                      <div key={country.country} className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="w-6 h-6 rounded-full bg-secondary flex items-center justify-center text-xs text-white">
                            {index + 1}
                          </span>
                          <span className="text-white">{country.country}</span>
                        </div>
                        <span className="text-muted-foreground">{country.percentage}%</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </motion.div>

              {/* Gender Distribution */}
              <motion.div variants={itemVariants}>
                <Card className="bg-card border-border/50">
                  <CardHeader>
                    <CardTitle className="text-lg font-semibold text-white">Gender Distribution</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-center gap-8">
                      {Object.entries(analytics?.audience_demographics?.gender || {}).map(([gender, percentage]) => (
                        <div key={gender} className="text-center">
                          <div className="text-3xl font-bold text-white">{percentage}%</div>
                          <div className="text-sm text-muted-foreground capitalize">{gender}</div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </div>
          </TabsContent>
        </Tabs>
      )}

      {/* Connect Dialog */}
      <ConnectDialog
        open={connectDialogOpen}
        onOpenChange={setConnectDialogOpen}
        username={username}
        setUsername={setUsername}
        accountType={accountType}
        setAccountType={setAccountType}
        setAsDefault={setAsDefault}
        setSetAsDefault={setSetAsDefault}
        connecting={connecting}
        onConnect={handleConnect}
        isFirstAccount={accounts.length === 0}
      />

      {/* Disconnect Dialog */}
      <Dialog open={disconnectDialogOpen} onOpenChange={setDisconnectDialogOpen}>
        <DialogContent className="bg-card border-border/50">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-white">Disconnect Account?</DialogTitle>
            <DialogDescription>
              This will remove @{accountToDisconnect?.tiktok_username} and all its imported video data. 
              You can reconnect anytime.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDisconnectDialogOpen(false)} className="border-border/50">
              Cancel
            </Button>
            <Button onClick={handleDisconnect} variant="destructive">
              Disconnect
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}

// Connect Dialog Component
function ConnectDialog({
  open,
  onOpenChange,
  username,
  setUsername,
  accountType,
  setAccountType,
  setAsDefault,
  setSetAsDefault,
  connecting,
  onConnect,
  isFirstAccount,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border/50">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-white flex items-center gap-2">
            <TikTokLogo className="w-6 h-6" />
            Connect TikTok Account
          </DialogTitle>
          <DialogDescription>
            Enter your TikTok username to connect your account.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label className="text-white">TikTok Username</Label>
            <Input
              placeholder="@yourusername"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="h-12 bg-secondary/50 border-border/50"
              data-testid="tiktok-username-input"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-white">Account Type</Label>
            <Select value={accountType} onValueChange={setAccountType}>
              <SelectTrigger className="h-12 bg-secondary/50 border-border/50" data-testid="account-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="creator">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    Creator Account
                  </div>
                </SelectItem>
                <SelectItem value="business">
                  <div className="flex items-center gap-2">
                    <Briefcase className="w-4 h-4" />
                    Business Account
                  </div>
                </SelectItem>
                <SelectItem value="personal">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Personal Account
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {!isFirstAccount && (
            <div className="flex items-center justify-between p-3 bg-secondary/20 rounded-lg">
              <div className="flex items-center gap-2">
                <Star className="w-4 h-4 text-[#00F0FF]" />
                <Label className="text-white cursor-pointer" htmlFor="set-default">Set as default account</Label>
              </div>
              <Switch
                id="set-default"
                checked={setAsDefault}
                onCheckedChange={setSetAsDefault}
                data-testid="set-default-switch"
              />
            </div>
          )}

          <div className="p-3 bg-[#00F0FF]/10 border border-[#00F0FF]/30 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-[#00F0FF] mt-0.5" />
              <p className="text-xs text-muted-foreground">
                <span className="text-white font-medium">Demo Mode:</span> This uses simulated data. 
                For real TikTok integration, TikTok Developer API credentials are required.
              </p>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-border/50"
          >
            Cancel
          </Button>
          <Button
            onClick={onConnect}
            disabled={connecting || !username}
            className="bg-gradient-to-r from-[#FF0050] to-[#00F2EA] hover:opacity-90 text-white"
            data-testid="confirm-connect-btn"
          >
            {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Connect"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
